import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, LegaProjectStatus, projectService } from '../services/database/projects';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Calendar,
  MessageSquare, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  ChevronRight,
  Plus,
  Search,
  MessageCircle,
  ArrowLeft,
  Users,
  Briefcase,
  TrendingUp,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const statusIcons = {
  new: Clock,
  contacted: MessageSquare,
  meeting_scheduled: Calendar,
  in_development: AlertTriangle,
  completed: CheckCircle2,
  archived: CheckCircle2
};

const statusColors = {
  new: 'text-blue-400 bg-[rgb(28,28,30)]/[0.54] border-blue-400/20',
  contacted: 'text-purple-400 bg-[rgb(28,28,30)]/[0.54] border-purple-400/20',
  meeting_scheduled: 'text-yellow-400 bg-[rgb(28,28,30)]/[0.54] border-yellow-400/20',
  in_development: 'text-emerald-400 bg-[rgb(28,28,30)]/[0.54] border-emerald-400/20',
  completed: 'text-green-400 bg-[rgb(28,28,30)]/[0.54] border-green-400/20',
  archived: 'text-[rgb(152,152,157)] bg-[rgb(28,28,30)]/[0.54] border-[rgb(152,152,157)]/20'
};

const statusLabels = {
  new: 'Novo Lead',
  contacted: 'Contatado',
  meeting_scheduled: 'Reunião Agendada',
  in_development: 'Em Desenvolvimento',
  completed: 'Concluído',
  archived: 'Arquivado'
};

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<LegaProjectStatus | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalLeads: 0,
    inDevelopment: 0,
    completedProjects: 0,
    conversionRate: 0
  });
  const [activeStatusProject, setActiveStatusProject] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    // Calcular métricas quando os projetos mudarem
    if (projects.length > 0) {
      const totalLeads = projects.filter(p => ['new', 'contacted', 'meeting_scheduled'].includes(p.status)).length;
      const inDevelopment = projects.filter(p => p.status === 'in_development').length;
      const completed = projects.filter(p => p.status === 'completed').length;
      const conversionRate = (completed / projects.length) * 100;

      setMetrics({
        totalLeads,
        inDevelopment,
        completedProjects: completed,
        conversionRate
      });
    }
  }, [projects]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeStatusProject) {
        const target = event.target as HTMLElement;
        if (!target.closest('.status-badge')) {
          setActiveStatusProject(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeStatusProject]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      toast.error('Erro ao carregar projetos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (projectId: string, status: LegaProjectStatus) => {
    try {
      await projectService.updateProjectStatus(projectId, status);
      toast.success('Status atualizado com sucesso!');
      loadProjects();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleWhatsAppClick = (phone: string) => {
    // Remover todos os caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Adicionar o código do país (Brasil - 55) se não existir
    const formattedPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
    
    window.open(`https://wa.me/${formattedPhone}`, '_blank');
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.lead?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.lead?.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !selectedStatus || project.status === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  const StatusBadge = ({ status, onClick }: { status: LegaProjectStatus; onClick?: () => void }) => {
    const Icon = statusIcons[status];
    return (
      <button 
        onClick={onClick}
        className={`px-4 py-2 rounded-2xl text-[13px] flex items-center gap-2 backdrop-blur-xl border ${statusColors[status]} transition-all duration-200 hover:bg-[rgb(44,44,46)] status-badge`}
      >
        <Icon size={16} />
        {statusLabels[status]}
        <ChevronDown size={14} className="ml-1" />
      </button>
    );
  };

  const StatusPopover = ({ project, onStatusChange, onClose }: { project: Project; onStatusChange: (status: LegaProjectStatus) => void; onClose: () => void }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100]"
        />
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute right-0 top-[calc(100%+8px)] z-[110] bg-[rgb(28,28,30)] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-1.5 min-w-[180px] shadow-2xl"
          onClick={handleClick}
        >
          {Object.entries(statusLabels).map(([value, label]) => {
            const Icon = statusIcons[value as LegaProjectStatus];
            return (
              <button
                key={value}
                onClick={() => {
                  onStatusChange(value as LegaProjectStatus);
                  onClose();
                }}
                className={`w-full px-3 py-2 text-[13px] rounded-xl flex items-center gap-2 ${
                  project.status === value 
                    ? 'bg-[rgb(44,44,46)] text-white' 
                    : 'text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)] hover:text-white'
                } transition-all duration-200`}
              >
                <Icon size={16} className={project.status === value ? 'text-white' : 'text-[rgb(152,152,157)]'} />
                {label}
              </button>
            );
          })}
        </motion.div>
      </>
    );
  };

  const MetricCard = ({ title, value, icon: Icon, description }: { title: string, value: string | number, icon: any, description?: string }) => (
    <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6 hover:bg-[rgb(44,44,46)] transition-all duration-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[rgb(152,152,157)] text-[13px]">{title}</p>
          <p className="text-[28px] font-semibold mt-1 text-white">{value}</p>
          {description && <p className="text-[rgb(152,152,157)] text-[13px] mt-1">{description}</p>}
        </div>
        <div className="p-3 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl">
          <Icon size={20} className="text-[rgb(152,152,157)]" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative w-full h-full">
      {/* Gradiente no topo */}
      <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-blue-900/30 via-blue-800/10 to-transparent pointer-events-none" />

      <div className="relative p-8 max-w-[1200px] mx-auto">
        <div className="space-y-8">
          <div>
            <h1 className="text-[28px] font-semibold text-white">Projetos</h1>
            <p className="text-[rgb(152,152,157)] text-[17px]">Gerencie os leads e projetos gerados pela Lega</p>
          </div>

          {/* Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              title="Total de Leads"
              value={metrics.totalLeads}
              icon={Users}
              description="Leads ativos no funil"
            />
            <MetricCard
              title="Em Desenvolvimento"
              value={metrics.inDevelopment}
              icon={Briefcase}
              description="Projetos em produção"
            />
            <MetricCard
              title="Taxa de Conversão"
              value={`${metrics.conversionRate.toFixed(1)}%`}
              icon={TrendingUp}
              description={`${metrics.completedProjects} projetos concluídos`}
            />
          </div>

          {/* Filtros */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[rgb(152,152,157)]" size={20} />
              <input
                type="text"
                placeholder="Buscar por título, nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-12 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl pl-12 pr-4 text-white placeholder-[rgb(152,152,157)] focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200"
              />
            </div>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as LegaProjectStatus)}
              className="h-12 px-4 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl text-white focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 min-w-[180px]"
            >
              <option value="">Todos os status</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Lista de Projetos */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                <p className="mt-4 text-gray-400">Carregando projetos...</p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400">Nenhum projeto encontrado</p>
              </div>
            ) : (
              filteredProjects.map(project => (
                <div
                  key={project.id}
                  className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6 hover:bg-[rgb(44,44,46)] transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 flex-1 min-w-0 mr-4">
                      <h3 className="text-[20px] font-semibold text-white truncate">{project.title}</h3>
                      <p className="text-[rgb(152,152,157)] text-[13px] truncate">
                        {project.lead?.name} • {project.lead?.email}
                      </p>
                      {project.business_idea && (
                        <p className="text-[rgb(152,152,157)] text-[13px] mt-2 line-clamp-2">
                          {project.business_idea}
                        </p>
                      )}
                    </div>
                    <div className="relative flex-shrink-0">
                      <StatusBadge 
                        status={project.status} 
                        onClick={() => setActiveStatusProject(activeStatusProject === project.id ? null : project.id)}
                      />
                      <AnimatePresence>
                        {activeStatusProject === project.id && (
                          <StatusPopover 
                            project={project}
                            onStatusChange={(status) => handleStatusChange(project.id, status)}
                            onClose={() => setActiveStatusProject(null)}
                          />
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-4">
                    <button
                      onClick={() => handleWhatsAppClick(project.lead?.phone || '')}
                      className="px-4 py-2 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl text-[13px] text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)] transition-all duration-200"
                    >
                      <MessageCircle size={16} className="inline-block mr-2" />
                      WhatsApp
                    </button>

                    <button
                      onClick={() => navigate(`/app/projects/${project.id}/canvas`)}
                      className="px-4 py-2 bg-white hover:bg-white/90 text-black rounded-2xl text-[13px] font-medium transition-all duration-200"
                    >
                      Ver detalhes
                      <ChevronRight size={16} className="inline-block ml-2" />
                    </button>
                  </div>

                  {project.meeting_date && (
                    <div className="mt-4 flex items-center gap-2 text-[13px] text-yellow-400 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-yellow-400/20 rounded-2xl px-4 py-2">
                      <Calendar size={16} />
                      <span>
                        Reunião agendada para {format(new Date(project.meeting_date), "dd 'de' MMMM', às' HH:mm", { locale: ptBR })}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;